import { Injector, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { catchError, filter, of, take, timeout } from 'rxjs';

/**
 * Waits for a signal to meet a specified condition with reactive observation
 * @param signal The signal to observe
 * @param isValid Predicate function to check if the signal value meets the condition
 * @param errorMessage Error message to log on timeout
 * @param defaultValue Default value to return if the condition is not met within the timeout
 * @param injector Angular injector required for toObservable to work outside injection context
 * @param timeoutMs Timeout duration in milliseconds (default: 5000ms)
 * @returns Observable that emits when the condition is met or timeout occurs
 *
 * @example
 * ```typescript
 * waitForSignalValue(
 *   authStore.user,
 *   (user) => !!user,
 *   'Timeout waiting for user',
 *   null,
 *   injector
 * )
 * ```
 */
export function waitForSignalValue<T>(
  signal: Signal<T>,
  isValid: (value: T) => boolean,
  errorMessage: string,
  defaultValue: T,
  injector: Injector,
  timeoutMs = 5000
) {
  const currentValue = signal();
  if (isValid(currentValue)) {
    return of(currentValue);
  }

  return toObservable(signal, { injector }).pipe(
    filter(isValid),
    timeout(timeoutMs),
    take(1),
    catchError((error: unknown) => {
      console.error(errorMessage, error);
      return of(signal() ?? defaultValue);
    })
  );
}
